import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorText,
  apiLoadErrorText,
  useGetEffectiveGroupRatesQuery,
  useListGroupRateObjectsQuery,
  useSeedFleetGroupsMutation,
  type FleetGroupSeedResult,
  type GroupRateKind,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { pluralizeRu } from '@shared/lib/formatFile';
import { useFocusBackOnClose } from '@shared/lib/useFocusBackOnClose';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconArrowRight, IconSparkles } from '@shared/ui/icons';
import { riseIn } from '@shared/theme/animations';
import {
  ButtonLink,
  Notice,
  RATE_KIND_LABEL,
  TextButton,
  effectiveRateRows,
  fleetLeading,
  fleetType,
  formatCount,
  isIsoDate,
  isRecord,
  rateKindCounts,
  stretchButtons,
  todayIso,
  typo,
} from '@entities/fleet';
import { RateDrawer, type RateView } from './RateDrawer';
import { RatesSummary } from './RatesSummary';
import { RatesTable } from './RatesTable';
import { RatesToolbar } from './RatesToolbar';
import { DEFAULT_OBJECT, scopeFromParam, type RateTarget } from './scope';

const NARROW = '640px';

/** Метка в истории: панель открыли из таблицы — закрытие возвращает «Назад». */
function isCardState(state: unknown): boolean {
  return isRecord(state) && state.rateCard === true;
}

/** Кнопка группы в таблице — куда вернуть фокус, если строку перерисовали. */
const rateButton = (id: string) => `[data-rate-open="${CSS.escape(id)}"]`;

const Root = styled.div`
  container: rates / inline-size;
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const TableHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  margin: ${({ theme }) => `${theme.spacing(6)} 0 ${theme.spacing(2)}`};

  & > .count {
    margin: 0;
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }

  & > .count strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  & > a {
    margin-left: auto;
  }

  @container rates (max-width: ${NARROW}) {
    & > a {
      margin-left: 0;
    }
  }
`;

const Card = styled.div`
  max-width: 680px;
  margin-top: ${({ theme }) => theme.spacing(6)};
  padding: ${({ theme }) => theme.spacing(6)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};

  & > h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
    text-wrap: balance;
  }

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(3)};
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > .actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
    margin-top: ${({ theme }) => theme.spacing(4)};
  }

  & > .actions + div {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }

  @container rates (max-width: ${NARROW}) {
    padding: ${({ theme }) => theme.spacing(4)};

    & > .actions {
      ${stretchButtons}
    }
  }
`;

function TableSkeleton() {
  return (
    <Section aria-busy="true" aria-label="Загрузка ставок">
      <SkeletonLine $height={72} />
      {Array.from({ length: 6 }, (_, i) => (
        <SkeletonLine key={i} $height={20} $width={i % 3 === 0 ? '45%' : '100%'} />
      ))}
    </Section>
  );
}

function seedText(r: FleetGroupSeedResult): string {
  if (r.groups_created === 0 && r.models_attached === 0) {
    return 'Новых групп нет: свободных моделей с должностью механизатора не нашлось.';
  }
  return typo(
    `Подобрано групп: ${formatCount(r.groups_created)}, моделей разложено: ${formatCount(r.models_attached)}. Теперь можно задавать ставки.`,
  );
}

/**
 * Ставки групп механизаторов (ТЗ п. 6.3.2–6.3.3): выбор объекта или ставок по
 * умолчанию для холдинга и даты, таблица групп с откуда взятой ставкой,
 * счётчик групп без ставки, панель «задать ставку» с подсказкой и историей.
 * Объект, дата и открытая группа — в адресе: ссылку можно переслать.
 */
export function GroupRates({ canWrite }: { canWrite: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const scope = scopeFromParam(params.get('object'));
  const today = todayIso();
  const onParam = params.get('on');
  const on = onParam && isIsoDate(onParam) ? onParam : today;
  const openGroup = params.get('group');
  const view: RateView = params.get('view') === 'history' ? 'history' : 'new';
  useFocusBackOnClose(openGroup, rateButton);

  const [filter, setFilter] = useState<GroupRateKind | null>(null);
  const [seedResult, setSeedResult] = useState<FleetGroupSeedResult | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  const lastObject = useRef<string | null>(null);
  const projectId = scope.kind === 'project' ? scope.projectId : null;
  useEffect(() => {
    if (projectId) lastObject.current = projectId;
  }, [projectId]);

  const objectsQ = useListGroupRateObjectsQuery();
  const objects = objectsQ.data?.items ?? [];
  // Ставки на дату: с объектом — объекта и по умолчанию, без объекта — только
  // ставки по умолчанию для холдинга.
  const effectiveQ = useGetEffectiveGroupRatesQuery(
    projectId ? { project_id: projectId, on } : { on },
    { skip: scope.kind === 'none' },
  );
  const [seed, { isLoading: seeding }] = useSeedFleetGroupsMutation();

  const rows = useMemo(
    () => (scope.kind === 'none' ? [] : effectiveRateRows(effectiveQ.data?.items ?? [])),
    [scope.kind, effectiveQ.data],
  );
  const counts = useMemo(() => rateKindCounts(rows), [rows]);
  // «Ставка объекта» у холдинга не бывает — такой фильтр там не действует.
  const activeFilter = scope.kind === 'default' && filter === 'project' ? null : filter;
  const visible = activeFilter ? rows.filter((r) => r.state.kind === activeFilter) : rows;

  const loading = scope.kind !== 'none' && effectiveQ.isLoading;
  const loadError = scope.kind !== 'none' ? effectiveQ.error : undefined;
  const fetching = effectiveQ.isFetching;

  const go = useCallback(
    (
      change: (p: URLSearchParams) => void,
      opts: { replace: boolean; state?: unknown } = { replace: true },
    ) => {
      const p = new URLSearchParams(location.search);
      change(p);
      const s = p.toString();
      navigate({ search: s ? `?${s}` : '' }, opts);
    },
    [location.search, navigate],
  );

  const dropDrawer = (p: URLSearchParams) => {
    p.delete('group');
    p.delete('view');
  };

  function setObject(id: string | null) {
    setFilter(null);
    go((p) => {
      if (id) p.set('object', id);
      else p.delete('object');
      dropDrawer(p);
    });
  }

  // Объект — как в шапке ОСНОВЫ: открыли экран без объекта или сменили объект
  // в шапке — ставки этого объекта; выбрали объект здесь — он же и в шапке.
  // «По умолчанию для холдинга» шапку не трогает.
  const header = useActiveProjectContext();
  const headerId = header.activeProjectId;
  const lastHeader = useRef(headerId);
  useEffect(() => {
    const changed = lastHeader.current !== headerId;
    lastHeader.current = headerId;
    if (!headerId || projectId === headerId) return;
    if (scope.kind === 'none' || changed) setObject(headerId);
    // setObject меняет адрес; следим только за шапкой и выбранным объектом.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerId, projectId, scope.kind]);

  function pickObject(id: string | null) {
    setObject(id);
    if (id && id !== DEFAULT_OBJECT) {
      const name = objects.find((o) => o.id === id)?.name;
      header.setActiveProject(id, name !== undefined ? { name } : undefined);
    }
  }

  function setScopeKind(kind: 'project' | 'default') {
    if (kind === 'default') {
      if (scope.kind !== 'default') setObject(DEFAULT_OBJECT);
      return;
    }
    if (scope.kind === 'default') setObject(lastObject.current);
  }

  function setDate(value: string) {
    go((p) => {
      if (isIsoDate(value) && value !== today) p.set('on', value);
      else p.delete('on');
    });
  }

  function openRate(groupId: string, next: RateView) {
    go(
      (p) => {
        p.set('group', groupId);
        p.set('view', next);
      },
      { replace: false, state: { rateCard: true } },
    );
  }

  const setView = (next: RateView) =>
    go(
      (p) => {
        p.set('view', next);
      },
      { replace: true, state: location.state },
    );

  const closeRate = useCallback(() => {
    if (isCardState(location.state)) navigate(-1);
    else go(dropDrawer);
  }, [location.state, navigate, go]);

  function refreshRows() {
    if (scope.kind !== 'none') void effectiveQ.refetch();
  }

  async function onSeed() {
    setSeedError(null);
    setSeedResult(null);
    try {
      const res = await seed().unwrap();
      setSeedResult(res.result);
    } catch (err) {
      setSeedError(apiErrorText(err, 'Не удалось подобрать группы по должностям механизаторов.'));
    }
  }

  const object = projectId ? objects.find((o) => o.id === projectId) : undefined;
  const target: RateTarget | null =
    scope.kind === 'project'
      ? { kind: 'project', projectId: scope.projectId, name: object?.name ?? 'объект' }
      : scope.kind === 'default'
        ? { kind: 'default' }
        : null;

  return (
    <Root>
      <RatesToolbar
        scope={scope}
        objects={objects}
        objectsLoading={objectsQ.isLoading}
        onScopeKind={setScopeKind}
        onObject={pickObject}
        on={on}
        onDate={setDate}
      />
      {objectsQ.isError && (
        <Section>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                objectsQ.error,
                'Не удалось загрузить объекты.',
                'Нет доступа к ставкам Экономиста.',
              )}
            </span>
            <TextButton type="button" onClick={() => void objectsQ.refetch()}>
              Повторить
            </TextButton>
          </Notice>
        </Section>
      )}

      {scope.kind === 'none' ? (
        <Card>
          <h2>Выберите объект</h2>
          <p>
            {typo(
              'Покажем ставки всех групп механизаторов на объекте на выбранную дату: своя ставка объекта, ставка по умолчанию для холдинга или ставки нет.',
            )}
          </p>
          <p>
            {typo(
              'Ставки по умолчанию для холдинга действуют там, где у объекта своей ставки нет, — их задают отдельно.',
            )}
          </p>
          <div className="actions">
            <Button type="button" $variant="secondary" onClick={() => setObject(DEFAULT_OBJECT)}>
              Ставки по умолчанию для холдинга
            </Button>
          </div>
        </Card>
      ) : loadError !== undefined ? (
        <Section>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                loadError,
                'Не удалось загрузить ставки групп.',
                'Нет доступа к ставкам Экономиста.',
              )}
            </span>
            <TextButton type="button" onClick={refreshRows}>
              Повторить
            </TextButton>
          </Notice>
        </Section>
      ) : loading ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <Card>
          <h2>Групп механизаторов пока нет</h2>
          <p>
            {typo(
              'Ставки задаются по группам механизаторов — профессиям, которые знает Хронум. Подбор по должностям механизаторов разложит модели справочника техники по группам; потом здесь можно будет задать ставки.',
            )}
          </p>
          <div className="actions">
            {canWrite && (
              <Button
                type="button"
                $variant="primary"
                disabled={seeding}
                onClick={() => void onSeed()}
              >
                <IconSparkles size={16} />
                {seeding ? 'Подбираю…' : 'Подобрать по должностям механизаторов'}
              </Button>
            )}
            <ButtonLink to="/agents/econ/fleet?tab=groups" $variant="secondary">
              Открыть группы механизаторов
              <IconArrowRight size={16} />
            </ButtonLink>
          </div>
          {(seedResult || seedError) && (
            <Notice $tone={seedError ? 'danger' : 'success'} role={seedError ? 'alert' : 'status'}>
              <span className="text">{seedError ?? (seedResult ? seedText(seedResult) : '')}</span>
            </Notice>
          )}
        </Card>
      ) : (
        <>
          <Section>
            <RatesSummary
              scope={scope.kind}
              counts={counts}
              filter={activeFilter}
              onFilter={setFilter}
              on={on}
            />
          </Section>
          <TableHead>
            <p className="count" aria-live="polite">
              {activeFilter ? (
                <>
                  Показаны: <strong>{RATE_KIND_LABEL[activeFilter]}</strong> ·{' '}
                  {formatCount(visible.length)} из {formatCount(rows.length)}{' '}
                  <TextButton type="button" onClick={() => setFilter(null)}>
                    показать все
                  </TextButton>
                </>
              ) : (
                <>
                  <strong>
                    {formatCount(rows.length)}{' '}
                    {pluralizeRu(rows.length, ['группа', 'группы', 'групп'])}
                  </strong>{' '}
                  механизаторов
                </>
              )}
            </p>
            <ButtonLink to="/agents/econ/fleet?tab=groups" $variant="ghost">
              Группы механизаторов
              <IconArrowRight size={16} />
            </ButtonLink>
          </TableHead>
          <div aria-busy={fetching}>
            <RatesTable
              rows={visible}
              canWrite={canWrite}
              showFallback={scope.kind === 'project'}
              onOpen={openRate}
            />
          </div>
        </>
      )}

      {target && openGroup && (
        <RateDrawer
          target={target}
          groupId={openGroup}
          row={rows.find((r) => r.groupId === openGroup)}
          rowsLoading={loading}
          on={on}
          canWrite={canWrite}
          view={view}
          onView={setView}
          onClose={closeRate}
          onRefreshRows={refreshRows}
        />
      )}
    </Root>
  );
}
